import React from "react"
import PropTypes from "prop-types"
import {get} from "lodash"
import {RouteComponentProps} from "react-router-dom"

import DealByUrlSlug from "@irati/deals/apollo/query/DealByUrlSlug"
import {Row, Card, Col} from "@irati/ui/src/Layout"
import DealStockCountByUrlSlug from "@irati/deals/apollo/query/DealStockCountByUrlSlug"
import history from "@irati/utils/history"
import {Deal} from "@irati/types"
import Spacing from "@irati/ui/src/Spacing"
import {shouldDisplayDealAvatar} from "@irati-web/perks/src/utils/deals/imageUtils"
import {determineDealThumbnailImage} from "@irati/deals/helpers/deals/appearance"
import withBranding, {WithBranding} from "@irati/brand/withBranding"
import authentication from "@irati/auth"
import tracking from "@irati-tracking/perks/perks"
import basket from "@irati/checkout/utils/basket"
import redemptionFlow from "@irati/checkout/utils/redemption"
import detailsToWebAddress from "@irati/checkout/utils/redemption/helpers/detailsToWebAddress"
import withPerkOrReward, {WithPerkOrReward} from "@irati-web/flexi-rewards/src/utils/withPerkOrReward"

import {hitLimits} from "@irati/checkout/utils/deal/limits"
import MobileFlushCol from "../../components/ui/MobileFlushCol"
import Container from "../../components/ui/RedemptionFlowContainer"
import DealProductForm from "../../components/DealSelectProducts/ProductForms"
import StockNotificationPopup from "../../components/DealSelectProducts/ProductForms/shared/StockNotificationPopup"
import Continue from "../../components/DealSelectProducts/Continue"
import DealAvatar from "../../components/DealSelectProducts/DealLogo"
import RequireDealStatusOn from "../../components/RequireDealStatusOn"
import OrderSummary from "../../components/OrderSummary/OrderSummary"
import DealBanner from "../../components/DealBanner"

export type OnProductSelectSubmit = () => Promise<any>

interface Props extends RouteComponentProps<{urlSlug: string}>, WithBranding, WithPerkOrReward {}

const SelectProducts: React.FC<Props> = (props, context) => {
  const urlSlug = props.match.params.urlSlug

  const handleSubmit =
    (deal: Deal): OnProductSelectSubmit =>
    async () => {
      const {reachedMaxPrice, reachedMinPrice} = hitLimits(deal)
      if (reachedMinPrice && !reachedMaxPrice) {
        const details = await redemptionFlow.selectProducts(deal)
        const url = detailsToWebAddress(details)

        tracking.redeemPerkOrderProduct(deal)

        if (deal.earnPoints) {
          if (basket.isCashback) {
            tracking.clickContinueWithFlexiPointsOption(deal)
          } else {
            tracking.clickContinueWithDiscountOption(deal)
          }
        }

        history.push(url, context)
      } else {
        throw new Error("perks.productForms.openValueProduct.maxValue.validation")
      }
    }

  return (
    <>
      <DealByUrlSlug variables={{urlSlug, isRewards: props.isRewards}}>
        {({data}) => {
          if (!data || !data.dealBySlug) return <RequireDealStatusOn deal={null} />
          const deal = data.dealBySlug

          const dealThumbnailSrc = shouldDisplayDealAvatar(deal) ? determineDealThumbnailImage(deal, props.branding) : null
          const showSavingOptions = deal.earnPoints && !!authentication.apps("wallet.treatTokens")

          return (
            <>
              <DealBanner deal={deal} showFavouriteIcon={false} />
              <Container>
                <Row>
                  <MobileFlushCol xs={12} md={8}>
                    <Card marginBottom={4}>
                      <Row>
                        <Col xs={12}>
                          <DealProductForm deal={deal} />
                        </Col>
                      </Row>
                    </Card>
                  </MobileFlushCol>
                  <MobileFlushCol xs={12} md={4}>
                    <Card>
                      <Row>
                        {dealThumbnailSrc && (
                          <Col xs={12}>
                            <DealAvatar src={dealThumbnailSrc} />
                          </Col>
                        )}

                        {/* Desktop Order Summary */}
                        <Col xsHidden smHidden md={12} id="dealSelectProductsContainerId">
                          <Spacing marginBottom={4}>
                            <OrderSummary deal={deal} showSavingOptions={showSavingOptions} />
                          </Spacing>
                          <Continue onClick={handleSubmit(deal)} />
                        </Col>

                        {/* Mobile Order Summary */}
                        <Col xs={12} mdHidden lgHidden>
                          <Spacing marginBottom={4}>
                            <OrderSummary deal={deal} isMobile showSavingOptions={showSavingOptions} />
                          </Spacing>
                          <Continue onClick={handleSubmit(deal)} />
                        </Col>
                      </Row>
                    </Card>
                  </MobileFlushCol>
                </Row>
              </Container>
            </>
          )
        }}
      </DealByUrlSlug>

      <DealStockCountByUrlSlug useLoader={false} useError={false} fetchPolicy="cache-and-network" variables={{urlSlug}}>
        {(stockCount) => get(stockCount, "data.dealStockCountByUrlSlug.allOutOfStock", false) && <StockNotificationPopup />}
      </DealStockCountByUrlSlug>
    </>
  )
}

SelectProducts.contextTypes = {
  appPath: PropTypes.string
}

export default withPerkOrReward(withBranding(SelectProducts))
